import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/lib/auth";
import { getPostAuthDestination } from "@/lib/postAuthRouting";
import { WattsLogo } from "@/components/WattsLogo";
import { SocialProof } from "@/components/SocialProof";

export const Route = createFileRoute("/auth")({
  component: AuthPage,
});

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.45c-.28 1.46-1.12 2.69-2.38 3.52v2.93h3.85c2.25-2.08 3.57-5.13 3.57-8.69z"/>
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.85-2.93c-1.08.72-2.45 1.16-4.08 1.16-3.13 0-5.79-2.11-6.74-4.96H1.27v3.11C3.25 21.3 7.31 24 12 24z"/>
      <path fill="#FBBC05" d="M5.26 14.36c-.24-.72-.38-1.49-.38-2.36s.14-1.64.38-2.36V6.53H1.27C.46 8.14 0 9.97 0 12s.46 3.86 1.27 5.47l3.99-3.11z"/>
      <path fill="#EA4335" d="M12 4.74c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.25 2.7 1.27 6.53l3.99 3.11C6.21 6.85 8.87 4.74 12 4.74z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="white" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.43 2.24-1.21 3.04-.84.87-2.18 1.55-3.29 1.45-.13-1.1.42-2.27 1.18-3.05.84-.88 2.27-1.53 3.32-1.44zM20.5 17.5c-.55 1.27-.81 1.84-1.52 2.97-.99 1.58-2.39 3.54-4.12 3.55-1.54.02-1.93-1-4.01-.99-2.08.01-2.51 1.01-4.05.99-1.73-.02-3.05-1.79-4.04-3.37C.13 16.65-.26 12.13 1.39 9.7c1.17-1.73 3.02-2.74 4.76-2.74 1.77 0 2.88.98 4.34.98 1.42 0 2.28-.98 4.33-.98 1.54 0 3.17.84 4.34 2.29-3.81 2.09-3.19 7.54.34 8.25z"/>
    </svg>
  );
}

function AuthPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptAge, setAcceptAge] = useState(false);
  const [acceptGeo, setAcceptGeo] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [nicknameError, setNicknameError] = useState("");
  const [checkingNickname, setCheckingNickname] = useState(false);
  const nickCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const TERMS_VERSION = "1.0";

  useEffect(() => {
    if (!user) return;
    if (!user.email_confirmed_at) { navigate({ to: "/verify-email" }); return; }
    getPostAuthDestination(user.id).then((to) => navigate({ to }));
  }, [user, navigate]);


  const prefs = (() => {
    if (typeof window === "undefined") return null;
    try { return JSON.parse(localStorage.getItem("watts_prefs") || "null"); } catch { return null; }
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tab === "register" && (!acceptTerms || !acceptAge)) {
      setShowErrors(true);
      toast.error("Debes aceptar los Términos y confirmar que eres mayor de 18 años.");
      return;
    }
    setLoading(true);
    try {
      if (tab === "register") {
        if (nickname.trim().length < 3) throw new Error("Nickname debe tener 3+ caracteres.");
        const nowIso = new Date().toISOString();
        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/map`,
            data: {
              nickname: nickname.trim(),
              preferred_modality: prefs?.modalities?.[0] ?? "road",
              preferred_pace: prefs?.pace ?? "medium",
              accepted_terms: true,
              accepted_terms_version: TERMS_VERSION,
              accepted_terms_at: nowIso,
              accepted_age: true,
              marketing_opt_in: acceptMarketing,
              geolocation_opt_in: acceptGeo,
            },
          },
        });
        if (error) throw error;

        // Capture IP (best-effort) and persist legal consent trail
        let ip: string | null = null;
        try {
          const r = await fetch("https://api.ipify.org?format=json");
          if (r.ok) ip = (await r.json())?.ip ?? null;
        } catch { /* ignore */ }

        const userId = signUpData.user?.id;
        if (userId) {
          await supabase.from("user_consents" as any).insert({
            user_id: userId,
            accepted_terms: true,
            accepted_terms_version: TERMS_VERSION,
            accepted_at: nowIso,
            accepted_ip: ip,
            accepted_age: true,
            marketing_consent: acceptMarketing,
            marketing_consent_at: acceptMarketing ? nowIso : null,
            geolocation_consent: acceptGeo,
            geolocation_consent_at: acceptGeo ? nowIso : null,
          });
        }

        toast.success("Revisa tu email para confirmar tu cuenta");
        navigate({ to: "/verify-email" });
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err: any) {
      toast.error(err.message || "Algo se ha torcido.");
    } finally {
      setLoading(false);
    }
  };


  const handleOAuth = async (provider: "google" | "apple") => {
    const result = await lovable.auth.signInWithOAuth(provider, {
      redirect_uri: window.location.origin + "/map",
    });
    if (result.error) toast.error("No se pudo iniciar sesión.");
  };

  const inputCls =
    "w-full rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] px-4 text-white placeholder:text-[#666] outline-none transition-colors focus:border-[#F5C518] focus:ring-2 focus:ring-[#F5C518]/30";

  return (
    <div className="flex min-h-screen flex-col bg-[#0f0f0f] fade-in">
      {/* TOP SECTION */}
      <div className="flex flex-col items-center px-6 pt-8 pb-3 text-center">
        <div className="max-[480px]:hidden"><WattsLogo width={200} /></div>
        <div className="hidden max-[480px]:block"><WattsLogo width={220} /></div>
        <div className="mt-4 w-full max-w-xs">
          <SocialProof />
        </div>
      </div>

      {/* Divider */}
      <div className="flex justify-center">
        <div className="h-px w-10 bg-[#F5C518]/70" />
      </div>

      {/* BOTTOM SECTION */}
      <div className="flex-1 px-6 pt-4 pb-[max(env(safe-area-inset-bottom),1.5rem)]">
        <div className="mx-auto w-full max-w-sm">
          <div className="grid grid-cols-2 rounded-full bg-[#1a1a1a] p-1">
            {(["login", "register"] as const).map((tabId) => (
              <button
                key={tabId}
                onClick={() => setTab(tabId)}
                className={`rounded-full py-2.5 text-sm font-medium transition-colors ${
                  tab === tabId ? "bg-[#F5C518] text-black" : "text-[#888]"
                }`}
              >
                {tabId === "login" ? t("auth.login") : t("auth.register")}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-3">
            {tab === "register" && (
              <div>
                <input
                  value={nickname}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNickname(v);
                    setNicknameError("");
                    if (nickCheckRef.current) clearTimeout(nickCheckRef.current);
                    const trimmed = v.trim();
                    if (trimmed.length < 3) return;
                    setCheckingNickname(true);
                    nickCheckRef.current = setTimeout(async () => {
                      const { data } = await supabase.rpc("nickname_exists" as any, { _nickname: trimmed });
                      setCheckingNickname(false);
                      if (data === true) setNicknameError("Este nombre ya está en uso");
                    }, 400);
                  }}
                  placeholder="Nombre o nickname"
                  required
                  style={{ height: 52 }}
                  className={inputCls}
                />
                {nicknameError && (
                  <p className="mt-1 text-xs text-red-500">{nicknameError}</p>
                )}
              </div>
            )}
            <input
              type="email" inputMode="email" autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.email")}
              required
              style={{ height: 52 }}
              className={inputCls}
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete={tab === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.password")}
                required minLength={8}
                style={{ height: 52 }}
                className={inputCls + " pr-12"}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-[#F5C518]"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {tab === "register" && (
              <div className="space-y-2.5 pt-1">
                {/* 1. Términos y Política de Privacidad (obligatorio) */}
                <label className="flex items-start gap-2.5 text-xs text-[#bbb] leading-snug cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className={`mt-0.5 h-4 w-4 shrink-0 accent-[#F5C518] cursor-pointer ${showErrors && !acceptTerms ? "ring-2 ring-red-500 rounded-sm" : ""}`}
                  />
                  <span>
                    He leído y acepto los{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#F5C518] underline">Términos y Condiciones</a>
                    {" "}y la{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#F5C518] underline">Política de Privacidad</a>.
                    <span className="text-[#F5C518]"> *</span>
                  </span>
                </label>

                {/* 2. Mayoría de edad (obligatorio) */}
                <label className="flex items-start gap-2.5 text-xs text-[#bbb] leading-snug cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptAge}
                    onChange={(e) => setAcceptAge(e.target.checked)}
                    className={`mt-0.5 h-4 w-4 shrink-0 accent-[#F5C518] cursor-pointer ${showErrors && !acceptAge ? "ring-2 ring-red-500 rounded-sm" : ""}`}
                  />
                  <span>
                    Confirmo que soy mayor de 18 años.
                    <span className="text-[#F5C518]"> *</span>
                  </span>
                </label>

                {/* 3. Geolocalización (opcional) */}
                <label className="flex items-start gap-2.5 text-xs text-[#888] leading-snug cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptGeo}
                    onChange={(e) => setAcceptGeo(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#F5C518] cursor-pointer"
                  />
                  <span>Permito el uso de mi ubicación para mostrar rutas y salidas cercanas (opcional).</span>
                </label>

                {/* 4. Marketing (opcional) */}
                <label className="flex items-start gap-2.5 text-xs text-[#888] leading-snug cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptMarketing}
                    onChange={(e) => setAcceptMarketing(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#F5C518] cursor-pointer"
                  />
                  <span>Quiero recibir novedades, noticias y comunicaciones comerciales de Watts (opcional).</span>
                </label>

                {showErrors && (!acceptTerms || !acceptAge) && (
                  <p className="text-xs text-red-500 pt-1">
                    Debes marcar las casillas obligatorias para continuar.
                  </p>
                )}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || (tab === "register" && (!acceptTerms || !acceptAge || !!nicknameError || checkingNickname))}
              style={{ height: 52 }}
              className="press-scale w-full rounded-xl bg-[#F5C518] font-bold text-black disabled:opacity-50"
            >
              {loading ? t("auth.loading") : tab === "login" ? t("auth.login") : t("auth.createAccount")}
            </button>
            {tab === "login" && (
              <div className="text-center pt-1">
                <Link to="/forgot-password" className="text-xs text-[#888] hover:text-[#F5C518] underline">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-[#666]">
            <span className="h-px flex-1 bg-[#2a2a2a]" />
            <span>{t("auth.orContinue")}</span>
            <span className="h-px flex-1 bg-[#2a2a2a]" />
          </div>

          <div className="space-y-3">
            <button
              onClick={() => handleOAuth("google")}
              style={{ height: 52 }}
              className="press-scale flex w-full items-center justify-center gap-2.5 rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] font-medium text-white"
            >
              <GoogleIcon /> {t("auth.google")}
            </button>
            <button
              onClick={() => handleOAuth("apple")}
              style={{ height: 52 }}
              className="press-scale flex w-full items-center justify-center gap-2.5 rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] font-medium text-white"
            >
              <AppleIcon /> {t("auth.apple")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
